/**
 * Optimized Product Enhancements - Version 2.0
 * - Performance optimized with event delegation and lazy execution
 * - DOM operations minimized and batched for best performance
 * - Memory usage optimized for mobile devices
 * - Code structured for better maintainability and debugging
 * - Event handling improved for better reliability
 * - Gallery and media compatibility assured
 * - Runtime diagnostics and self-healing capabilities
 */

// Use immediately-invoked function expression (IIFE) to avoid global scope pollution
(function() {
  // Configuration object for easy maintenance and customization
  const config = {
    selectors: {
      // Use more specific selectors where possible
      productPage: [
        '[data-product-form]', 
        '.product', 
        '.product-template',
        '.template-product main',
        '.product-page'
      ],
      mediaContainer: [
        '.product__media-wrapper',
        '.product__media-list',
        '.product-media-container',
        '.product-single__media-wrapper',
        '.product-single__photo-wrapper',
        '.product__images',
        '.product-gallery__viewport'
      ],
      productMediaSelectors: [
        '.product__media', 
        '.media-wrapper',
        '.product__main-col',
        '.product-single__media',
        '.product-gallery',
        '.product-images',
        '.product__images'
      ],
      pickupAvailabilitySelectors: [
        '.pickup-availability-preview',
        '.pickup-availability',
        '.pickup-availability-button',
        '[data-pickup-availability-container]',
        '[data-store-availability-container]',
        '.product__pickup-availabilities',
        '.product__pickup-availabilities-container'
      ],
      priceElements: [
        '.price', 
        '[data-product-price]',
        '.product__price',
        '.product-price'
      ],
      addToCartBtn: [
        'button[name="add"]', 
        '[data-testid="Checkout-button"]',
        '.add-to-cart',
        '.product-form__add-button'
      ],
      conditionLabel: [
        '[data-product-condition-label]', 
        '.product_condition_label',
        '.condition-label'
      ],
      galleryContainers: [
        '.product-gallery', 
        '.product__media-gallery',
        '.product-single__photos',
        '.product-images',
        '.slider-mobile-gutter',
        '.product-single__media-group',
        '.gallery-viewer',
        '.product__slideshow'
      ],
      gallerySlides: [
        '.product__media',
        '.product-single__media',
        '.slick-slide',
        '.product__slide',
        '.product-single__photo',
        '.gallery__cell',
        '.gallery__item',
        '.carousel-cell'
      ]
    },
    styleId: 'visual-enhancements-styles',
    badges: {
      product: '<div class="badge-pulse"></div><svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="display:inline-block;vertical-align:middle;margin-right:6px"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#4a90e2" stroke-width="2"/><path d="M8 12L11 15L16 9" stroke="#4a90e2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Retourpareltje</span>'
    },
    textMatches: {
      prices: ['Normale prijs', 'Aanbiedingsprijs', 'Regular price', 'Sale price', 'Aanbieding'],
      condition: ['Staat van het product'],
      pickup: ['Afhaling is beschikbaar'],
      trust: ['Gratis verzending', 'dagen bedenktijd', 'jaar garantie']
    },
    animations: {
      delayShort: 300,
      delayMedium: 500,
      delayLong: 800
    },
    debug: false // Set to true to enable debug logs
  };

  // Performance monitoring
  const performance = {
    startTime: Date.now(),
    timers: {},
    start: function(label) {
      if (!config.debug) return;
      this.timers[label] = performance.now();
    },
    end: function(label) {
      if (!config.debug) return;
      if (this.timers[label]) {
        console.log(`⏱️ ${label}: ${(performance.now() - this.timers[label]).toFixed(2)}ms`);
        delete this.timers[label];
      }
    },
    log: function(message) {
      if (!config.debug) return;
      console.log(`🔍 ${message}`);
    }
  };

  // DOM ready function - more reliable than load or DOMContentLoaded
  function onDOMReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  // First check if we're on a product page before doing any work
  function isProductPage() {
    performance.start('isProductPage');
    // Use faster location check first before DOM queries
    if (window.location.pathname.includes('/products/') || 
        window.location.pathname.includes('/product/')) {
      performance.end('isProductPage');
      return true;
    }
    
    // Use any() pattern to check selectors efficiently
    const result = config.selectors.productPage.some(selector => document.querySelector(selector) !== null);
    performance.end('isProductPage');
    return result;
  }
  
  // Utility: DOM element creator with attributes and event handling
  function createElement(tag, attributes = {}, events = {}, children = []) {
    const element = document.createElement(tag);
    
    // Apply attributes
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'className') {
        element.className = value;
      } else if (key === 'innerHTML') {
        element.innerHTML = value;
      } else if (key === 'style') {
        Object.entries(value).forEach(([prop, val]) => {
          element.style[prop] = val;
        });
      } else {
        element.setAttribute(key, value);
      }
    });
    
    // Add event listeners
    Object.entries(events).forEach(([event, handler]) => {
      element.addEventListener(event, handler);
    });
    
    // Append children (if any)
    children.forEach(child => {
      if (typeof child === 'string') {
        element.appendChild(document.createTextNode(child));
      } else if (child) {
        element.appendChild(child);
      }
    });
    
    return element;
  }
  
  // Utility: Find first matching element from array of selectors
  function findFirstElement(selectorArray) {
    for (const selector of selectorArray) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    return null;
  }
  
  // Utility: Safely add class to element
  function addClass(element, className) {
    if (element && className) {
      element.classList.add(className);
    }
  }
  
  // Utility: Safely hide element
  function hideElement(element) {
    if (element) {
      element.style.display = 'none';
    }
  }

  // Utility: Detect if element is part of a gallery
  function isGalleryElement(element) {
    if (!element) return false;
    
    // Check if element itself is a gallery element
    if (element.classList && (
        element.classList.contains('slick-slide') ||
        element.classList.contains('flickity-slider') ||
        element.classList.contains('gallery__cell') ||
        element.classList.contains('product__slide') ||
        element.hasAttribute('data-media-id') ||
        element.hasAttribute('data-gallery-config')
    )) {
      return true;
    }
    
    // Check if element is inside a gallery
    return !!element.closest('.slick-initialized, .flickity-enabled, [data-gallery-role], .product-gallery, .product__slideshow');
  }

  // Utility: Check if an element is visible
  function isVisible(element) {
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0';
  }

  // Create and inject stylesheet - enhanced to check for existing styles
  function injectStylesheet() {
    performance.start('injectStylesheet');
    if (document.getElementById(config.styleId)) {
      performance.end('injectStylesheet');
      return;
    }
    
    const styles = createElement('style', {
      id: config.styleId,
      innerHTML: getEnhancementStyles()
    });
    
    document.head.appendChild(styles);
    performance.end('injectStylesheet');
  }
  
  // Hide original price elements and pickup availability
  function cleanupOriginalElements() {
    performance.start('cleanupOriginalElements');
    // FIXED: Don't query media containers for hiding, only specific pickup elements
    const pickupSelectors = config.selectors.pickupAvailabilitySelectors;
    
    // FIXED: Use specific pickup selectors instead of media container selectors
    const pickupElements = document.querySelectorAll(pickupSelectors.join(','));
    
    // Only look for price and pickup text in non-essential elements
    // More complex selector to avoid targeting gallery elements
    const elementsToProcess = document.querySelectorAll('div:not([class*="gallery"]):not([class*="slider"]):not([class*="slide"]):not([class*="carousel"]), span, p');
    
    // Create documentFragment for better performance if modifying many elements
    const toHide = [];
    
    // Process elements in batches for less layout thrashing
    elementsToProcess.forEach(el => {
      // Skip processing if element is part of image gallery or slider
      if (isGalleryElement(el)) {
        return;
      }
      
      const text = el.textContent || '';
      
      // Check for price text patterns
      if (config.textMatches.prices.some(pattern => text.includes(pattern))) {
        // Only hide if not our custom price element
        if (!el.classList.contains('price-comparison') && 
            !el.closest('.price-comparison') && 
            !isGalleryElement(el)) {
          toHide.push(el);
        }
      }
      
      // Check for pickup availability text
      if (config.textMatches.pickup.some(pattern => text.includes(pattern))) {
        // Don't hide if it's a container with other important content
        const childElements = el.querySelectorAll('*').length;
        if (childElements < 5 && !isGalleryElement(el)) {
          toHide.push(el);
        }
      }
    });
    
    // Hide elements in a single batch
    requestAnimationFrame(() => {
      toHide.forEach(hideElement);
      pickupElements.forEach(hideElement);
    });
    
    performance.end('cleanupOriginalElements');
  }
  
  // Add badge to product image (optimized finding strategy)
  function addProductBadge() {
    performance.start('addProductBadge');
    // Try media container selectors, but don't use product media selectors
    // to avoid conflicts with actual media elements
    const mediaContainer = findFirstElement(config.selectors.mediaContainer);
    
    // If we found a container, add the badge
    if (mediaContainer) {
      performance.log('Found media container for badge');
      
      // Only set position relative if it's not already positioned
      const computedStyle = window.getComputedStyle(mediaContainer);
      if (computedStyle.position === 'static') {
        mediaContainer.style.position = 'relative';
      }
      
      // Check if badge already exists
      if (!mediaContainer.querySelector('.product-badge')) {
        const badge = createElement('div', {
          className: 'product-badge',
          innerHTML: config.badges.product
        });
        
        mediaContainer.appendChild(badge);
      }
    } else {
      // Fallback to add badge to any product image container found
      const productImgContainers = document.querySelectorAll('.product__photo-container, .product-single__photos, .product-image-container');
      if (productImgContainers.length > 0) {
        const container = productImgContainers[0];
        
        // Only set position relative if needed
        const computedStyle = window.getComputedStyle(container);
        if (computedStyle.position === 'static') {
          container.style.position = 'relative';
        }
        
        // Check if badge already exists
        if (!container.querySelector('.product-badge')) {
          const badge = createElement('div', {
            className: 'product-badge',
            innerHTML: config.badges.product
          });
          
          // Add media query for desktop
          if (window.matchMedia('(min-width: 768px)').matches) {
            badge.style.right = '20%';
          }
          
          container.appendChild(badge);
        }
      } else {
        // Last resort: find individual product images
        const productImages = document.querySelectorAll('.product__media img, .product-single__photo img, .product-featured-media, .product-image');
        
        if (productImages.length > 0) {
          const firstImage = productImages[0];
          const firstImageParent = firstImage.parentElement;
          
          if (firstImageParent) {
            // Only set position relative if needed
            const computedStyle = window.getComputedStyle(firstImageParent);
            if (computedStyle.position === 'static') {
              firstImageParent.style.position = 'relative';
            }
            
            // Check if badge already exists
            if (!firstImageParent.querySelector('.product-badge')) {
              const badge = createElement('div', {
                className: 'product-badge',
                innerHTML: config.badges.product
              });
              
              // Add media query for desktop
              if (window.matchMedia('(min-width: 768px)').matches) {
                badge.style.right = '20%';
              }
              
              firstImageParent.appendChild(badge);
            }
          }
        }
      }
    }
    performance.end('addProductBadge');
  }
  
  // Enhance product condition banner (more efficient selector)
  function enhanceConditionBanner() {
    performance.start('enhanceConditionBanner');
    // More specific selectors for better performance
    const candidates = document.querySelectorAll('.custom-liquid, .product-condition-banner, .product-condition');
    
    candidates.forEach(banner => {
      if (banner.enhanced) return; // Skip if already enhanced
      
      const bannerContent = banner.textContent || '';
      
      if (bannerContent.includes(config.textMatches.condition[0])) {
        addClass(banner, 'product-condition-banner');
        banner.enhanced = true; // Mark as enhanced to avoid duplicate processing
        
        // Add subtle animation to highlight it
        setTimeout(() => {
          addClass(banner, 'banner-loaded');
        }, config.animations.delayShort);
      }
    });
    performance.end('enhanceConditionBanner');
  }
  
  // Add quality indicator (optimized DOM insertion)
  function addQualityIndicator() {
    performance.start('addQualityIndicator');
    const conditionLabel = findFirstElement(config.selectors.conditionLabel);
    
    if (conditionLabel && !document.querySelector('.quality-indicator')) {
      const qualityIndicator = createElement('div', {
        className: 'quality-indicator',
        innerHTML: `
          <div class="indicator-header">
            <p style="margin-bottom:5px; font-weight:500;">Productkwaliteit:</p>
            <span class="quality-badge">Excellent</span>
          </div>
          <div style="position:relative; height:10px; background-color:#edf2f7; border-radius:5px; overflow:hidden; margin-bottom:6px;">
            <div class="quality-bar"></div>
          </div>
          <div style="display:flex; justify-content:space-between; margin-top:5px; font-size:0.8em; color:#718096;">
            <span>Basis</span>
            <span>Goed</span>
            <span style="font-weight:bold; color:#4a90e2;">Uitstekend</span>
          </div>
        `
      });
      
      // Insert before or after condition label
      if (conditionLabel.parentNode) {
        conditionLabel.parentNode.insertBefore(qualityIndicator, conditionLabel.nextSibling);
        
        // Animate quality bar after delay (using requestAnimationFrame for better performance)
        setTimeout(() => {
          requestAnimationFrame(() => {
            const qualityBar = qualityIndicator.querySelector('.quality-bar');
            if (qualityBar) {
              qualityBar.style.width = '95%';
            }
          });
        }, config.animations.delayShort);
      }
    }
    performance.end('addQualityIndicator');
  }
  
  // Create enhanced price comparison (optimized price extraction)
  function createPriceComparison() {
    performance.start('createPriceComparison');
    // Only create if we don't already have our custom price element
    if (document.querySelector('.enhanced-price-comparison')) {
      performance.end('createPriceComparison');
      return;
    }
    
    const priceElement = findFirstElement(config.selectors.priceElements);
    
    if (priceElement) {
      // Improved price extraction with better prioritization
      // First try to get price from data attributes
      let currentPrice = priceElement.getAttribute('data-price') || 
                         priceElement.getAttribute('content');
      
      // If no attribute data, try text extraction with regex
      if (!currentPrice) {
        const priceMatch = (priceElement.textContent || '').match(/[€$£]?\s*\d+(?:[.,]\d+)?/);
        currentPrice = priceMatch ? priceMatch[0] : null;
      }
      
      // Try to get compare price from related elements
      let comparePrice = priceElement.getAttribute('data-compare-price') || 
                        document.querySelector('[data-compare-price]')?.getAttribute('data-compare-price');
      
      // If not found, try other common selectors
      if (!comparePrice) {
        const comparePriceElement = document.querySelector('.compare-price, .compare-at-price, .product__compare-price, [data-compare-at-price]');
        if (comparePriceElement) {
          const priceMatch = (comparePriceElement.textContent || '').match(/[€$£]?\s*\d+(?:[.,]\d+)?/);
          comparePrice = priceMatch ? priceMatch[0] : null;
        }
      }
      
      // Extract numeric values more robustly
      let currentPriceValue = 0;
      let comparePriceValue = 0;
      
      if (typeof currentPrice === 'string') {
        currentPriceValue = parseFloat(currentPrice.replace(/[^0-9.,]/g, '').replace(',', '.'));
      }
      
      if (typeof comparePrice === 'string') {
        comparePriceValue = parseFloat(comparePrice.replace(/[^0-9.,]/g, '').replace(',', '.'));
      }
      
      // Create enhanced price element if we have valid prices
      if (currentPriceValue > 0) {
        const priceComparisonEl = createElement('div', {
          className: 'enhanced-price-comparison'
        });
        
        if (comparePriceValue > 0 && comparePriceValue > currentPriceValue) {
          const discount = Math.round((comparePriceValue - currentPriceValue) / comparePriceValue * 100);
          
          priceComparisonEl.innerHTML = `
            <div class="price-label">Normale prijs</div>
            <div class="price-row">
              <span class="original-price">€${comparePriceValue.toFixed(2)}</span>
              <span class="current-price">€${currentPriceValue.toFixed(2)}</span>
              <span class="savings-badge">
                BESPAAR ${discount}%
              </span>
            </div>
          `;
        } else {
          priceComparisonEl.innerHTML = `
            <span class="single-price">€${currentPriceValue.toFixed(2)}</span>
          `;
        }
        
        // Add before price element
        if (priceElement.parentNode) {
          priceElement.parentNode.insertBefore(priceComparisonEl, priceElement);
        }
      }
    }
    performance.end('createPriceComparison');
  }
  
  // Create enhanced buttons (optimized event handling)
  function enhanceBuyButtons() {
    performance.start('enhanceBuyButtons');
    const addToCartBtn = findFirstElement(config.selectors.addToCartBtn);
    
    if (addToCartBtn && !document.querySelector('.modern-add-button')) {
      // Create enhanced add to cart button with optimized event handling
      const enhancedAddBtn = createElement('button', {
        type: 'button',
        className: 'modern-add-button',
        innerHTML: `
          <span class="button-text">Aan winkelwagen toevoegen</span>
          <svg class="cart-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h19l-3 10H6L3 6zm0 0l-.75-2.5M9 20a1 1 0 11-2 0 1 1 0 012 0zm9 0a1 1 0 11-2 0 1 1 0 012 0z" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `
      }, {
        click: function(e) {
          // Prevent double-clicks
          if (this.classList.contains('button-loading') || 
              this.classList.contains('button-success')) {
            e.preventDefault();
            return;
          }
          
          e.preventDefault();
          this.classList.add('button-loading');
          
          // Store reference for timeout cleanup
          const self = this;
          
          // Click the original button
          addToCartBtn.click();
          
          // Debounce timeout handles
          let loadingTimer;
          let successTimer;
          
          // Cleanup function for timers
          const cleanup = () => {
            clearTimeout(loadingTimer);
            clearTimeout(successTimer);
          };
          
          // Simulate response and give visual feedback
          loadingTimer = setTimeout(() => {
            self.classList.remove('button-loading');
            self.classList.add('button-success');
            
            // Reset after animation completes
            successTimer = setTimeout(() => {
              self.classList.remove('button-success');
            }, 1500);
          }, config.animations.delayLong);
          
          // Cleanup timers if page changes
          window.addEventListener('beforeunload', cleanup);
        }
      });
      
      // Container for enhanced buttons
      const buttonContainer = createElement('div', {
        className: 'enhanced-buttons-container'
      }, {}, [enhancedAddBtn]);
      
      // Add buy now button if dynamic checkout is available
      const checkoutBtn = document.querySelector('.shopify-payment-button__button');
      
      if (checkoutBtn) {
        const enhancedBuyBtn = createElement('button', {
          type: 'button',
          className: 'modern-buy-button',
          innerHTML: `
            <span class="button-text">Nu kopen</span>
            <svg class="cart-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `
        }, {
          click: function(e) {
            // Prevent double-clicks
            if (this.classList.contains('button-loading')) {
              e.preventDefault();
              return;
            }
            
            e.preventDefault();
            this.classList.add('button-loading');
            checkoutBtn.click();
            
            // Cleanup if navigation doesn't happen
            setTimeout(() => {
              this.classList.remove('button-loading');
            }, 3000);
          }
        });
        
        buttonContainer.appendChild(enhancedBuyBtn);
      }
      
      // Insert our buttons before the original
      if (addToCartBtn.parentNode) {
        addToCartBtn.parentNode.insertBefore(buttonContainer, addToCartBtn);
        
        // Hide originals using a single CSS rule set for performance
        const hideStyle = 'opacity:0;height:0;overflow:hidden;position:absolute;pointer-events:none;';
        
        // Apply styles efficiently
        requestAnimationFrame(() => {
          addToCartBtn.style.cssText = hideStyle;
          
          if (checkoutBtn) {
            const checkoutContainer = checkoutBtn.closest('.shopify-payment-button');
            if (checkoutContainer) {
              checkoutContainer.style.cssText = hideStyle;
            }
          }
        });
      }
    }
    performance.end('enhanceBuyButtons');
  }
  
  // Enhance trust indicators - replaced traversing with targeted selection
  function enhanceTrustIndicators() {
    performance.start('enhanceTrustIndicators');
    // More targeted approach to find trust indicators
    const trustTextNodes = [...document.querySelectorAll('p, div, span')].filter(el => {
      if (el.enhanced || isGalleryElement(el)) return false;
      
      const text = el.textContent || '';
      return config.textMatches.trust.some(pattern => text.includes(pattern)) && !el.querySelector('svg');
    });
    
    // Process in batches for better performance
    if (trustTextNodes.length > 0) {
      trustTextNodes.forEach(el => {
        // Don't enhance if it already has HTML or has been processed
        if (el.enhanced || el.innerHTML !== el.textContent) return;
        el.enhanced = true;
        
        // Transform checkmarks into SVG icons
        const enhancedText = el.textContent.replace(/✓\s*([^•]*)/g, function(match, content) {
          return `
            <div class="trust-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M5 13L9 17L19 7" stroke="#4a90e2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${content.trim()}</span>
            </div>
          `;
        });
        
        if (enhancedText !== el.textContent) {
          const container = createElement('div', {
            className: 'enhanced-trust-indicators',
            innerHTML: enhancedText
          });
          
          // Replace original element with enhanced version
          if (el.parentNode) {
            el.parentNode.replaceChild(container, el);
          }
        }
      });
    }
    performance.end('enhanceTrustIndicators');
  }
  
  // Add fade-in animations to elements (performance optimized)
  function addAnimations() {
    performance.start('addAnimations');
    // FIXED: More careful selector targeting that won't affect gallery functionality
    const animationTargets = {
      // Avoid selecting elements if they contain sliders or controls or are part of a gallery
      fadeIn: [...document.querySelectorAll('.product__media:not(.slick-slide):not([data-gallery-role]), .product-featured-media:not([data-gallery-role])')].filter(el => 
        !isGalleryElement(el) &&
        !el.closest('.slick-initialized') && 
        !el.closest('.flickity-enabled') && 
        !el.querySelector('.slick-track') &&
        !el.querySelector('.flickity-viewport')
      ),
      hoverCards: document.querySelectorAll('.multicolumn .column')
    };
    
    // Add classes in batches with requestAnimationFrame for better performance
    if (animationTargets.fadeIn.length > 0 || animationTargets.hoverCards.length > 0) {
      requestAnimationFrame(() => {
        // Only add animation to elements that aren't part of interactive galleries
        animationTargets.fadeIn.forEach(el => {
          // Additional check to ensure we're not breaking slider functionality
          if (!isGalleryElement(el) &&
              !el.hasAttribute('data-slick-index') && 
              !el.hasAttribute('aria-hidden') && 
              !el.hasAttribute('data-gallery-selected')) {
            addClass(el, 'fade-in-element');
          }
        });
        
        animationTargets.hoverCards.forEach(el => addClass(el, 'hover-effect-card'));
      });
    }
    
    // Find and enhance testimonial cards - FIXED: use vanilla JavaScript instead of jQuery selectors
    // First, select all paragraphs and divs
    const paragraphsAndDivs = document.querySelectorAll('p, div');
    // Then filter to only those containing the star emoji
    const starRatingElements = Array.from(paragraphsAndDivs).filter(el => 
      (el.textContent || '').includes('⭐')
    );
    
    starRatingElements.forEach(el => {
      const text = el.textContent || '';
      if (text.includes('⭐') && text.includes('"') && !el.closest('.testimonial-card')) {
        const parent = el.closest('.column, .grid__item, .testimonial');
        if (parent) addClass(parent, 'testimonial-card');
      }
    });
    performance.end('addAnimations');
  }
  
  // Apply subtle typography improvements (performance optimized)
  function improveTypography() {
    performance.start('improveTypography');
    // Use specific selectors and reduce reflows
    const elements = {
      productTitle: document.querySelector('.product__title h1, .product-single__title, h1.product-title'),
      infoWrapper: document.querySelector('.product__info-wrapper, .product-details, .product-single__meta'),
      conditionTexts: document.querySelectorAll('.product_condition_label, .product_condition_value, [data-product-condition]')
    };
    
    // Batch DOM operations
    if (elements.productTitle && !elements.productTitle.classList.contains('enhanced-product-title')) {
      addClass(elements.productTitle, 'enhanced-product-title');
    }
    
    if (elements.infoWrapper && !elements.infoWrapper.classList.contains('enhanced-info-wrapper')) {
      addClass(elements.infoWrapper, 'enhanced-info-wrapper');
      
      // Add subtle separators between sections
      const childDivs = elements.infoWrapper.querySelectorAll(':scope > div');
      childDivs.forEach((div, index) => {
        if (index < childDivs.length - 1 && !div.classList.contains('section-divider')) {
          addClass(div, 'section-divider');
        }
      });
    }
    
    // Enhance condition text
    elements.conditionTexts.forEach(el => {
      if (!el.classList.contains('enhanced-condition-text')) {
        addClass(el, 'enhanced-condition-text');
      }
    });
    performance.end('improveTypography');
  }
  
  // FIXED: Added safety check function to restore gallery if hidden
  function restoreGalleryIfHidden() {
    performance.start('restoreGalleryIfHidden');
    // Check if we're on a product page but gallery containers are hidden
    const gallerySelectors = config.selectors.galleryContainers;
    
    // Check if any gallery is present but hidden
    gallerySelectors.forEach(selector => {
      const galleries = document.querySelectorAll(selector);
      galleries.forEach(gallery => {
        if (gallery) {
          const computedStyle = window.getComputedStyle(gallery);
          // Only restore if explicitly set to none (not if it's hidden for responsive reasons)
          if (computedStyle.display === 'none' && gallery.style.display === 'none') {
            performance.log('Gallery found hidden, restoring:', selector);
            gallery.style.display = '';
          }
        }
      });
    });
    
    // Also check for individual slides/images that might be hidden
    const slideSelectors = config.selectors.gallerySlides;
    
    slideSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(slide => {
        if (isGalleryElement(slide)) {
          const computedStyle = window.getComputedStyle(slide);
          if (computedStyle.display === 'none' && 
              !slide.classList.contains('price-comparison') && 
              !slide.closest('.price-comparison') &&
              slide.style.display === 'none') {
            performance.log('Slide found hidden, restoring');
            slide.style.display = '';
          }
        }
      });
    });
    
    // Check if any slick/flickity carousels need reinitialization
    const carousels = document.querySelectorAll('.slick-initialized, .flickity-enabled');
    carousels.forEach(carousel => {
      // Check if carousel is broken (no visible slides)
      const slides = carousel.querySelectorAll('.slick-slide, .flickity-slider > *');
      const visibleSlides = [...slides].filter(slide => isVisible(slide));
      
      if (slides.length > 0 && visibleSlides.length === 0) {
        performance.log('Carousel found with no visible slides, attempting to reinitialize');
        
        // Try to refresh the carousel
        if (window.jQuery && carousel.classList.contains('slick-initialized')) {
          try {
            window.jQuery(carousel).slick('refresh');
            performance.log('Slick carousel refreshed');
          } catch(e) {
            performance.log('Error refreshing slick:', e);
          }
        } else if (carousel.classList.contains('flickity-enabled') && window.Flickity) {
          try {
            const flkty = window.Flickity.data(carousel);
            if (flkty) {
              flkty.resize();
              performance.log('Flickity carousel refreshed');
            }
          } catch(e) {
            performance.log('Error refreshing Flickity:', e);
          }
        }
      }
    });
    performance.end('restoreGalleryIfHidden');
  }
  
  // NEW: Add resize handling for better responsive behavior
  function setupResizeHandler() {
    // Debounce function to avoid performance issues
    function debounce(func, wait) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }
    
    // Handle resize events - updating position of elements that need it
    const handleResize = debounce(() => {
      // Update badge position on resize for better responsive display
      const badges = document.querySelectorAll('.product-badge');
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      
      badges.forEach(badge => {
        badge.style.right = isDesktop ? '20%' : '10px';
      });
      
      // Check for any galleries that need restoration after resize
      restoreGalleryIfHidden();
    }, 250); // 250ms debounce
    
    // Add resize listener
    window.addEventListener('resize', handleResize);
  }
  
  // CSS styles - moved to end of file for readability and added performance optimizations
  function getEnhancementStyles() {
    return `
      /* General Animations - optimized for performance */
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes pulseSavings {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      
      @keyframes pulse {
        0% { transform: scale(1); opacity: 0.6; }
        50% { transform: scale(1.2); opacity: 0.3; }
        100% { transform: scale(1); opacity: 0.6; }
      }
      
      @keyframes progressBar {
        0% { width: 0; }
        100% { width: 95%; }
      }
      
      @keyframes buttonLoader {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      /* Badge styling - will-change added for GPU acceleration */
      .product-badge {
        position: absolute;
        top: 10px;
        right: 10px;
        background-color: rgba(255,255,255,0.95);
        color: #4a90e2;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 0.85em;
        font-weight: 600;
        z-index: 100;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        display: flex;
        align-items: center;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        animation: fadeIn 0.5s ease-out forwards;
        will-change: transform, box-shadow;
        pointer-events: none; /* Prevent blocking clicks on gallery controls */
      }
      
      .product-badge:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .badge-pulse {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        background: rgba(74, 144, 226, 0.2);
        border-radius: 20px;
        z-index: -1;
        animation: pulse 2s infinite;
      }
      
      @media (min-width: 768px) {
        .product-badge {
          right: 20%; /* Adjust position for desktop */
        }
      }
      
      /* Enhanced price comparison - will-change for better performance */
      .enhanced-price-comparison {
        margin: 15px 0;
        animation: fadeIn 0.5s ease-out forwards;
        will-change: opacity, transform;
      }
      
      .price-label {
        margin-bottom: 5px;
        font-size: 0.85em;
        color: #718096;
      }
      
      .price-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
      }
      
      .original-price {
        text-decoration: line-through;
        color: #a0aec0;
        font-size: 1.1em;
      }
      
      .current-price {
        color: #2d3748;
        font-weight: bold;
        font-size: 1.6em;
      }
      
      .single-price {
        color: #2d3748;
        font-weight: bold;
        font-size: 1.6em;
      }
      
      .savings-badge {
        background: #48bb78;
        color: white;
        padding: 4px 10px;
        border-radius: 20px;
        font-size: 0.85em;
        font-weight: 500;
        animation: pulseSavings 2s infinite;
        will-change: transform;
      }
      
      /* Button styling - optimized with transitions and GPU acceleration */
      .enhanced-buttons-container {
        margin: 15px 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        max-width: 300px;
      }
      
      .modern-add-button, 
      .modern-buy-button {
        background-color: #4a90e2;
        color: white;
        border: none;
        border-radius: 6px;
        padding: 12px 24px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.3s ease;
        cursor: pointer;
        width: 100%;
        position: relative;
        will-change: transform, box-shadow;
      }
      
      .modern-add-button:hover {
        background-color: #3a7bc8;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
      
      .modern-buy-button {
        background-color: #48bb78;
      }
      
      .modern-buy-button:hover {
        background-color: #38a169;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
      
      .cart-icon {
        transition: transform 0.3s ease;
      }
      
      .modern-add-button:hover .cart-icon,
      .modern-buy-button:hover .cart-icon {
        transform: translateX(3px);
      }
      
      .button-loading .button-text,
      .button-loading .cart-icon,
      .button-success .button-text {
        opacity: 0;
      }
      
      .button-loading::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.3);
        border-top-color: white;
        animation: buttonLoader 0.8s infinite linear;
      }
      
      .button-success::after {
        content: '✓';
        position: absolute;
        font-size: 20px;
        color: white;
      }
      
      /* Product condition banner - optimized */
      .product-condition-banner {
        background-color: #f7f9fc !important;
        padding: 18px !important;
        margin: 25px 0 !important;
        border-radius: 8px !important;
        box-shadow: 0 2px 10px rgba(74,144,226,0.1) !important;
        border-left: 4px solid #4a90e2 !important;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        will-change: transform, box-shadow;
      }
      
      .banner-loaded {
        transform: translateY(0);
        opacity: 1;
      }
      
      .product-condition-banner:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(74,144,226,0.15) !important;
      }
      
      /* Quality indicator - transition optimized */
      .quality-indicator {
        margin: 15px 0;
        padding: 15px;
        background-color: #f8fafc;
        border-radius: 8px;
        animation: fadeIn 0.5s ease-out forwards;
      }
      
      .indicator-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .quality-badge {
        background-color: #4a90e2;
        color: white;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 0.7em;
        font-weight: 600;
      }
      
      .quality-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 0;
        background: linear-gradient(90deg, #4a90e2, #63b3ed);
        border-radius: 5px;
        transition: width 1s ease-out;
        will-change: width;
      }
      
      /* Trust indicators - optimized */
      .enhanced-trust-indicators {
        margin: 15px 0;
        padding: 10px 15px;
        background-color: #f8fafc;
        border-radius: 8px;
        animation: fadeIn 0.5s ease-out forwards;
      }
      
      .trust-item {
        display: flex;
        align-items: center;
        margin-bottom: 5px;
      }
      
      .trust-item svg {
        margin-right: 8px;
        flex-shrink: 0;
      }
      
      /* Testimonial cards - optimized */
      .testimonial-card {
        background: white;
        border-radius: 10px;
        padding: 20px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        position: relative;
        overflow: hidden;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        will-change: transform, box-shadow;
      }
      
      .testimonial-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
      }
      
      /* Typography & Layout Enhancement */
      .enhanced-product-title {
        font-size: 1.8rem !important;
        line-height: 1.2 !important;
        font-weight: 600 !important;
        color: #2d3748 !important;
      }
      
      .enhanced-info-wrapper {
        padding: 30px 20px;
      }
      
      .section-divider {
        margin-bottom: 20px !important;
        padding-bottom: 20px !important;
        border-bottom: 1px solid #f0f0f0 !important;
      }
      
      .enhanced-condition-text {
        font-size: 1.1rem !important;
      }
      
      /* Animations - optimized with will-change */
      .fade-in-element {
        animation: fadeIn 0.8s ease-in-out;
        will-change: opacity, transform;
      }
      
      /* Hover effects - optimized */
      .hover-effect-card {
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        will-change: transform, box-shadow;
      }
      
      .hover-effect-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
      }
      
      /* FIX: Only hide specific price elements, not gallery elements */
      /* Carefully targeting price elements only */
      .product__info-wrapper .price,
      .product__info-wrapper .price-item,
      .price__container:not(.gallery-container):not(.image-container):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]), 
      .price__regular:not(.gallery-item):not(.image-item):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]), 
      .price__sale:not(.gallery-item):not(.image-item):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]), 
      .price:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]), 
      .price-item:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]), 
      .price--listing:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]),
      .product-price:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]),
      .product-price-minimum:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]),
      .product-price__price:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]),
      .price-box:not([class*='gallery']):not([class*='image']):not([class*="slider"]):not([class*="slick"]):not([class*="carousel"]) {
        display: none;
      }
      
      /* Hide pickup availability */
      .pickup-availability-preview,
      .pickup-availability,
      .pickup-availability-button,
      [data-pickup-availability-container],
      [data-store-availability-container],
      .product__pickup-availabilities,
      .product__pickup-availabilities-container {
        display: none !important;
      }
      
      /* Show our custom price comparison */
      .enhanced-price-comparison,
      .price-comparison {
        display: block !important;
      }
      
      /* Make sure our enhancements never break layouts */
      .enhanced-price-comparison *,
      .product-badge *,
      .quality-indicator *,
      .modern-add-button *,
      .modern-buy-button * {
        box-sizing: border-box;
      }
      
      /* Responsive enhancements for mobile */
      @media (max-width: 767px) {
        .enhanced-price-comparison {
          margin: 10px 0;
        }
        
        .product-badge {
          top: 8px;
          right: 8px;
          padding: 4px 10px;
          font-size: 0.75em;
        }
        
        .quality-indicator,
        .enhanced-trust-indicators {
          padding: 12px;
        }
        
        .modern-add-button, 
        .modern-buy-button {
          padding: 10px 20px;
          font-size: 0.95em;
        }
      }
    `;
  }
  
  // Main function to run all enhancements
  function enhanceProductPage() {
    performance.start('enhanceProductPage');
    performance.log('Enhancing product page...');
    
    // First do non-intrusive enhancements
    addProductBadge();
    enhanceConditionBanner();
    addQualityIndicator();
    createPriceComparison();
    enhanceTrustIndicators();
    improveTypography();
    
    // Then do enhancements that might hide/replace elements
    enhanceBuyButtons();
    
    // Last do visual enhancements and animations
    addAnimations();
    
    // Then clean up unwanted elements, but carefully to not break gallery
    cleanupOriginalElements();
    
    // Add resize handling for better responsive behavior
    setupResizeHandler();
    
    performance.end('enhanceProductPage');
  }
  
  // Main initialization function
  function initialize() {
    performance.start('initialize');
    performance.log('Initializing product enhancements...');
    
    // Only run on product pages
    if (!isProductPage()) {
      performance.log('Not a product page, skipping enhancements');
      performance.end('initialize');
      return;
    }
    
    // Inject styles first to avoid FOUC (Flash of Unstyled Content)
    injectStylesheet();
    
    // Wait for the gallery or sliders to initialize before running enhancements
    // This prevents issues with image galleries disappearing
    const runEnhancements = () => {
      // Check if gallery is already initialized
      const galleryInitialized = document.querySelector('.slick-initialized, .flickity-enabled, [data-gallery-role="gallery"]');
      
      if (galleryInitialized) {
        performance.log('Gallery initialized, running enhancements');
        enhanceProductPage();
      } else {
        performance.log('Waiting for gallery initialization...');
        // Wait a bit longer for gallery to initialize
        setTimeout(() => {
          enhanceProductPage();
          
          // Run cleanup operations once more after a longer delay
          // to catch any dynamically loaded elements
          setTimeout(() => {
            cleanupOriginalElements();
            // Check if gallery is still working
            setTimeout(restoreGalleryIfHidden, 500);
          }, 1500);
        }, 500);
      }
    };
    
    // Use requestIdleCallback for better performance if available
    if ('requestIdleCallback' in window) {
      requestIdleCallback(runEnhancements);
    } else {
      setTimeout(runEnhancements, 100); // Increased timeout to ensure gallery initializes
    }
    
    performance.end('initialize');
  }
  
  // Start the enhancement process
  onDOMReady(initialize);
  
  // Add a small timeout as fallback for complex pages
  setTimeout(() => {
    if (document.readyState === 'complete' && isProductPage() && !document.querySelector('.product-badge')) {
      performance.log('Fallback initialization for product enhancements');
      initialize();
    }
  }, 1500); // Increased timeout to ensure everything is loaded
  
  // Run gallery restoration checks after a delay
  setTimeout(restoreGalleryIfHidden, 2000);
  setTimeout(restoreGalleryIfHidden, 5000); // Run again after 5 seconds to catch late-loading galleries
  
  // Handle page mutations for single-page applications
  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      // Use more intelligent detection based on URL changes or significant DOM changes
      const urlChanged = mutations.some(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'href') {
          return true;
        }
        return false;
      });
      
      // Check for significant DOM changes that might indicate page navigation
      const significantDomChange = mutations.some(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 5) {
          return true;
        }
        return false;
      });
      
      if ((urlChanged || significantDomChange) && isProductPage() && !document.querySelector('.product-badge')) {
        performance.log('SPA navigation detected to product page');
        // Use setTimeout to let the page render a bit first
        setTimeout(initialize, 300);
        // Also run gallery restoration after SPA navigation
        setTimeout(restoreGalleryIfHidden, 1500);
      }
    });
    
    // Start observing for SPA navigation with optimized options
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
      characterData: false
    });
  }
})();